export const UNITS_CONFIG_KEY = 'claim_item_line_units';
export const UNIT_CODE_MAX_LENGTH = 32;
// Labels are saved to the charge's `unit` column, which holds 32 characters.
export const UNIT_LABEL_MAX_LENGTH = 32;

/** What a charge's quantity counts, e.g. hours or pieces. */
export interface Unit {
  code: string;
  label: string;

  /** Used for a quantity of 1, e.g. "Hour"; falls back to the label. */
  singular?: string;
}

export interface UnitsConfig extends Record<string, unknown> {
  units?: Partial<Unit>[];
}

export interface Units {
  units: Unit[];
}

export type RetrieveUnitsResponse = Units;
export type UpdateUnitsRequest = Units;
export type UpdateUnitsResponse = Units;

/** Generic defaults; tenants add their own, like visits or square feet. */
export const DEFAULT_UNITS: Unit[] = [
  { code: 'HOUR', label: 'Hours', singular: 'Hour' },
  { code: 'FLAT', label: 'Flat rate' },
  { code: 'DAY', label: 'Days', singular: 'Day' },
  { code: 'PIECE', label: 'Pieces', singular: 'Piece' },
];

export function normalizeUnitCode(code?: string | null): string | null {
  const normalized = code?.trim().toUpperCase();
  return normalized ? normalized : null;
}

/** Drops blank, too-long or duplicate units; a bad singular is just left off. */
export function normalizeUnits(units?: ReadonlyArray<Partial<Unit> | null | undefined> | null): Unit[] {
  const seen = new Set<string>();
  const normalized: Unit[] = [];

  for (const unit of units ?? []) {
    const code = normalizeUnitCode(unit?.code);
    const label = unit?.label?.trim();

    if (!code || !label) continue;
    if (code.length > UNIT_CODE_MAX_LENGTH || label.length > UNIT_LABEL_MAX_LENGTH) continue;
    if (seen.has(code)) continue;

    const singular = unit?.singular?.trim();

    seen.add(code);
    normalized.push(singular && singular.length <= UNIT_LABEL_MAX_LENGTH ? { code, label, singular } : { code, label });
  }

  return normalized;
}

/** The tenant's units, or the defaults if none are usable. */
export function getUnits(config?: UnitsConfig | null): Units {
  const units = normalizeUnits(config?.units);
  return { units: units.length ? units : DEFAULT_UNITS };
}

/** Undefined when the tenant doesn't offer that unit. */
export function findUnit(units: Units, code?: string | null): Unit | undefined {
  const normalized = normalizeUnitCode(code);
  if (!normalized) return undefined;

  return units.units.find(unit => unit.code === normalized);
}

/** Singular for a quantity of 1, else the charge's saved label (kept if the unit was renamed or removed). */
export function unitLabelFor(
  units: Units,
  charge: { quantity?: number | null; unit?: string | null; unitCode?: string | null }
): string | null {
  const saved = charge.unit ?? null;
  if (charge.quantity !== 1) return saved;

  const unit = findUnit(units, charge.unitCode);
  return unit?.singular && unit.label === saved ? unit.singular : saved;
}
